import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { AUTH_SILENT_ATTEMPTED_KEY, isJwtExpired } from './token.util';

@Injectable({
  providedIn: 'root',
})
export class AuthRedirectGuard {
  constructor(
    private auth: AuthService,
    private router: Router
  ) { }

  redirectForTokenError(error: { error?: string; message?: string }): void {
    const errorCode = error?.error;

    if (errorCode === 'login_required') {
      sessionStorage.setItem(AUTH_SILENT_ATTEMPTED_KEY, '1');
      this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    if (errorCode === 'consent_required' || error?.message === 'Access token expired') {
      sessionStorage.removeItem(AUTH_SILENT_ATTEMPTED_KEY);
      this.auth.loginWithRedirect({
        authorizationParams: {
          ...(errorCode === 'consent_required' ? { prompt: 'consent' } : {}),
        },
        appState: {
          target: window.location.pathname + window.location.search,
        },
      });
      return;
    }

    this.router.navigate(['/'], { replaceUrl: true });
  }

  /** True when stored tokens exist but are no longer valid. */
  hasExpiredStoredTokens(): boolean {
    const access = localStorage.getItem('accessToken');
    const id = localStorage.getItem('idToken');
    if (access && isJwtExpired(access)) {
      return true;
    }
    if (id && isJwtExpired(id)) {
      return true;
    }
    return false;
  }
}



/** Auth0 / OIDC errors that are resolved by sending the user through login again. */
export function isAuthRecoverableError(error: unknown): boolean {
  const err = error as {
    error?: string;
    message?: string;
    error_description?: string;
    status?: number;
  };
  const code = err?.error ?? '';
  const text = `${err?.message ?? ''} ${err?.error_description ?? ''}`.toLowerCase();

  if (err?.status === 401) {
    return true;
  }

  if (
    code === 'consent_required' ||
    code === 'login_required' ||
    code === 'missing_refresh_token' ||
    code === 'invalid_grant' ||
    code === 'invalid_token'
  ) {
    return true;
  }

  return (
    text.includes('consent required') ||
    text.includes('login required') ||
    text.includes('missing refresh token') ||
    text.includes('expired') ||
    text.includes('id token') ||
    text.includes('access token') ||
    text.includes('unable to resolve') ||
    text.includes('token fetch')
  );
}

export function authErrorNeedsConsentPrompt(error: unknown): boolean {
  const err = error as { error?: string; message?: string };
  const code = err?.error ?? '';
  const msg = (err?.message ?? '').toLowerCase();
  return code === 'consent_required' || msg.includes('consent required');
}

